import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Tag } from "lucide-react";

import { useLang, useLangPrefix } from "@/contexts/lang";
import type { StrapiArticle } from "@/lib/articles";
import { getArticles, getCoverUrl } from "@/lib/articles";

export const Route = createFileRoute("/news/")({
	loader: async () => {
		const articles = await getArticles("mk-MK");
		return { articles };
	},
	headers: () => ({
		"Cache-Control":
			"public, max-age=0, s-maxage=3600, stale-while-revalidate=86400",
	}),
	head: () => ({
		meta: [
			{ title: "Вести | Македонијатурист АД" },
			{
				name: "description",
				content:
					"Најнови вести и известувања од Македонијатурист АД. Бидете во тек со нашите активности, реновирања и новини во гостопримството.",
			},
		],
		links: [
			{
				rel: "canonical",
				href: "https://makedonijaturist.com.mk/news",
			},
		],
	}),
	component: NewsPage,
});

export function NewsPageContent({ articles }: { articles: StrapiArticle[] }) {
	const t = useLang();
	const prefix = useLangPrefix();

	return (
		<main className="bg-white">
			{/* Hero section */}
			<section className="mt-grain relative overflow-hidden bg-primary text-white py-12 sm:py-16 md:py-20">
				{/* Animated background elements */}
				<div className="pointer-events-none absolute inset-0">
					<motion.div
						className="absolute -top-[20%] right-0 h-[400px] w-[400px] sm:h-[600px] sm:w-[600px] md:h-[800px] md:w-[800px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.15),transparent)] blur-[60px] sm:blur-[80px] md:blur-[100px]"
						animate={{
							scale: [1, 1.2, 1],
							opacity: [0.5, 0.8, 0.5],
						}}
						transition={{
							duration: 8,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					<motion.div
						className="absolute bottom-0 left-[-10%] h-[300px] w-[300px] sm:h-[450px] sm:w-[450px] md:h-[600px] md:w-[600px] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.08),transparent)] blur-[50px] sm:blur-[65px] md:blur-[80px]"
						animate={{
							scale: [1, 1.15, 1],
							x: [0, 30, 0],
						}}
						transition={{
							duration: 10,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					<motion.div
						className="absolute top-[40%] left-[20%] h-[150px] w-[150px] sm:h-[225px] sm:w-[225px] md:h-[300px] md:w-[300px] rounded-full bg-[radial-gradient(closest-side,rgba(201,161,90,0.08),transparent)] blur-[40px] sm:blur-[50px] md:blur-[60px]"
						animate={{
							y: [-20, 20, -20],
							x: [-10, 10, -10],
						}}
						transition={{
							duration: 12,
							repeat: Number.POSITIVE_INFINITY,
							ease: "easeInOut",
						}}
					/>
					<div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] sm:bg-[size:50px_50px] md:bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_70%)]" />
				</div>
				<div className="relative mx-auto max-w-7xl px-4 sm:px-6">
					<div className="max-w-3xl">
						<p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
							{t.newsPage.label}
						</p>
						<h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.08]">
							{t.newsPage.heading}
						</h1>
						<p className="mt-4 sm:mt-6 max-w-3xl text-base sm:text-lg text-white/80 font-light leading-relaxed md:text-xl">
							{t.newsPage.intro}
						</p>
					</div>
				</div>
			</section>

			{/* Articles grid */}
			<section className="py-12 sm:py-16 md:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{articles.map((article) => {
							const coverUrl = getCoverUrl(article);
							return (
								<a
									key={article.documentId}
									href={`${prefix}/news/${article.documentId}`}
									className="group"
								>
									<article className="relative overflow-hidden rounded-xl bg-white shadow-lg shadow-black/5 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-2 border border-black/5">
										{/* Image */}
										<div className="relative h-56 overflow-hidden bg-gradient-to-br from-secondary via-secondary/90 to-secondary/70">
											{coverUrl && (
												<img
													src={coverUrl}
													alt={article.cover?.alternativeText ?? article.title}
													className="absolute inset-0 w-full h-full object-cover"
												/>
											)}
											<div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />

											{!coverUrl && (
												<div className="absolute inset-0 flex items-center justify-center">
													<div className="text-center">
														<div className="w-16 h-16 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
															<span className="text-3xl text-primary/30">
																📰
															</span>
														</div>
														<p className="text-xs font-medium text-primary/40">
															{t.newsPage.newsImage}
														</p>
													</div>
												</div>
											)}

											{/* Category badge */}
											<div className="absolute top-4 left-4">
												<span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/90 backdrop-blur-sm px-3 py-1.5 text-xs font-medium text-primary shadow-lg">
													<Tag className="h-3 w-3" />
													{article.category}
												</span>
											</div>

											<div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
										</div>

										{/* Article content */}
										<div className="p-6 flex flex-col">
											<div className="mb-4">
												<h3 className="font-display text-xl font-medium tracking-tight text-primary mb-3 line-clamp-2 group-hover:text-accent transition-colors">
													{article.title}
												</h3>
												<div className="flex items-center gap-2 text-xs text-muted-foreground font-light mb-3">
													<Calendar className="h-3.5 w-3.5" />
													<span>{article.date}</span>
												</div>
											</div>

											<p className="text-sm text-foreground leading-relaxed font-light mb-6 line-clamp-3 flex-grow">
												{article.excerpt}
											</p>

											<div className="pt-4 border-t border-primary/5">
												<span className="inline-flex items-center gap-2 text-sm font-medium text-primary group-hover:text-accent transition-colors">
													{t.newsPage.readMore}
													<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
												</span>
											</div>
										</div>
									</article>
								</a>
							);
						})}
					</div>
				</div>
			</section>
		</main>
	);
}

function NewsPage() {
	const { articles } = Route.useLoaderData();
	return <NewsPageContent articles={articles} />;
}
